// libraries
import { AccelerateVehicleCondition } from '@makemydeal/dr-platform-types';
import type { IVehicle } from '@makemydeal/dr-platform-types';

// types
import type { AiAltVehicleOption } from '../types/AiAltVehicleOption';

/**
 * The recommendation's own quoted payment terms, ferried with the mapped `IVehicle` so the
 * "Add New Scenario" path can seed them instead of the deal's.
 */
export type RecommendedVehiclePayment = {
    downPayment: number;
    buyRate?: number;
    apr: number;
    sellRate?: number;
    moneyFactor?: number;
    residualPercent?: number;
};

/**
 * Maps an AVO recommendation's `vehicle` to the `IVehicle` shape `newVehicleSelected` consumes,
 * mirroring `dr-services-recommend`'s paymentService transforms (chromeId → positive chromeStyleId,
 * condition lowercased, odometer → mileage) so it passes `useValidateVehicle`'s identity + validity
 * gate. Also ferries the recommendation's quoted `recommendedPayment` for "Add New Scenario".
 */
export const mapAiAltVehicleOptionToVehicle = (
    recommendation: AiAltVehicleOption
): IVehicle & { recommendedPayment: RecommendedVehiclePayment } => {
    const { vehicle } = recommendation;

    const condition = vehicle.condition.toLowerCase();
    const isNewVehicle = condition === 'new';

    // The "Change" payment call reads `type` (not `condition`) plus `invoiceAmount`/`cost` via
    // `createVehicleForPaymentNode`; AVO recs lack these (inventory supplies them for standard search),
    // so set them here or Unifi returns NO_VALID_QUOTES. "cpo" folds onto CERTIFIED; new/used/certified
    // already match the lowercased `condition`.
    const type = condition === 'cpo' ? AccelerateVehicleCondition.CERTIFIED : (condition as AccelerateVehicleCondition);

    const parsedChromeStyleId = Number(vehicle.chromeId);
    const hasChromeStyleId = Number.isFinite(parsedChromeStyleId) && parsedChromeStyleId > 0;

    const mileage = vehicle.odometer ?? (isNewVehicle ? 0 : undefined);

    return {
        vin: vehicle.vin,
        ...(vehicle.stockNumber !== undefined && { stockNumber: vehicle.stockNumber }),
        ...(hasChromeStyleId && { chromeStyleId: parsedChromeStyleId }),
        condition,
        type,
        listedPrice: vehicle.listedPrice,
        retailPrice: vehicle.retailPrice,
        invoiceAmount: vehicle.invoicePrice,
        cost: vehicle.invoicePrice || undefined,
        ...(mileage !== undefined && { mileage }),
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        trim: vehicle.trim,
        exteriorColor: vehicle.exteriorColor,
        imageUrl: vehicle.imageUrl,
        recommendedPayment: {
            downPayment: recommendation.payment.downPayment,
            buyRate: recommendation.payment.buyRate,
            apr: recommendation.payment.apr,
            sellRate: recommendation.payment.sellRate,
            moneyFactor: recommendation.payment.moneyFactor,
            residualPercent: recommendation.payment.residualPercent
        }
    } as IVehicle & { recommendedPayment: RecommendedVehiclePayment };
};
